import {
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  keyframes,
  Portal,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { MdWarning, MdInfo, MdCheckCircle } from 'react-icons/md';
import { useEstadisticasDictamenes } from '../../hooks/useEstadisticasDictamenes';
import type { Dictamen } from './types';

interface AlertasDictamenesProps {
  dictamenes: Dictamen[];
}

const slideDown = keyframes`
  from { transform: translateY(-100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(0); opacity: 1; }
  to { transform: translateY(-100px); opacity: 0; }
`;

export default function AlertasDictamenes({ dictamenes }: AlertasDictamenesProps) {
  const estadisticas = useEstadisticasDictamenes(dictamenes);
  const [mostrarAlertas, setMostrarAlertas] = useState(true);
  const [animacionSalida, setAnimacionSalida] = useState(false);
  
  useEffect(() => {
    setMostrarAlertas(true);
    setAnimacionSalida(false);
    
    const timer = setTimeout(() => {
      setAnimacionSalida(true);
      setTimeout(() => {
        setMostrarAlertas(false);
      }, 300);
    }, 4700);
    
    return () => clearTimeout(timer);
  }, [dictamenes]);
  
  const alertas = [];

  // Alerta por bajo porcentaje de pagos
  if (estadisticas.porcentajePagado < 70) {
    alertas.push({
      status: 'warning' as const,
      icon: MdWarning,
      title: 'Porcentaje de pagos bajo',
      description: `Solo el ${estadisticas.porcentajePagado.toFixed(1)}% de los dictámenes están pagados`
    });
  }

  // Alerta por ingresos pendientes
  const ingresosPendientes = estadisticas.ingresoTotal - estadisticas.ingresosPagados;
  if (ingresosPendientes > 10000) {
    alertas.push({
      status: 'error' as const,
      icon: MdWarning,
      title: 'Ingresos pendientes altos',
      description: `$${ingresosPendientes.toFixed(2)} en pagos pendientes`
    });
  }

  // Alerta por dictámenes sin operador
  const sinOperador = estadisticas.porOperador['Sin asignar'] || 0;
  if (sinOperador > 0) {
    alertas.push({
      status: 'info' as const,
      icon: MdInfo,
      title: 'Dictámenes sin asignar',
      description: `${sinOperador} dictámenes necesitan ser asignados a un operador`
    });
  }

  // Mensaje positivo si todo está bien
  if (alertas.length === 0) {
    alertas.push({
      status: 'success' as const,
      icon: MdCheckCircle,
      title: 'Sistema funcionando correctamente',
      description: 'Todos los indicadores están dentro de los parámetros normales'
    });
  }

  if (alertas.length === 0 || !mostrarAlertas) return null;

  return (
    <Portal>
      <Box 
        position="fixed" 
        top="20px" 
        left="50%" 
        transform="translateX(-50%)" 
        zIndex={9999}
        w="auto"
        maxW="600px"
        animation={`${animacionSalida ? slideUp : slideDown} 0.3s ease-in-out`}
      >
        <VStack spacing={3} align="stretch">
          {alertas.map((alerta, index) => (
            <Alert key={index} status={alerta.status} borderRadius="md" shadow="lg">
              <AlertIcon as={alerta.icon} />
              <Box flex="1">
                <AlertTitle fontSize="sm">{alerta.title}</AlertTitle>
                <AlertDescription fontSize="xs">{alerta.description}</AlertDescription>
              </Box>
            </Alert>
          ))}
        </VStack>
      </Box>
    </Portal>
  );
}